import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material'
import { Redemption } from '../../api/types'
import { formatCzk, formatUnits4 } from './format'

interface ConfirmRedemptionDialogProps {
  open: boolean
  redemption: Redemption | null
  submitting: boolean
  onConfirm: (sendEmail: boolean) => void
  onClose: () => void
}

/**
 * Potvrzení odkupu (jen CEO) — dvojité potvrzení (nevratný zápis do core) +
 * checkbox „Poslat klientovi rozpis" (default zapnuto, tichý režim jde vypnout).
 * Vzor ConfirmBatchDialog z kontroly přidělení.
 */
export function ConfirmRedemptionDialog({
  open,
  redemption,
  submitting,
  onConfirm,
  onClose,
}: ConfirmRedemptionDialogProps) {
  const [secondStep, setSecondStep] = useState(false)
  const [sendEmail, setSendEmail] = useState(false)

  // Reset při každém otevření (druhý krok i default emailu).
  useEffect(() => {
    if (open) {
      setSecondStep(false)
      setSendEmail(true)
    }
  }, [open])

  const handleConfirm = () => {
    if (!secondStep) {
      setSecondStep(true)
      return
    }
    onConfirm(sendEmail)
  }

  const units = redemption ? formatUnits4(redemption.units) : ''
  const net = redemption ? formatCzk(redemption.netAmount) : ''
  const fee = redemption
    ? redemption.sfUnits
      ? `${formatCzk(redemption.sfFee)} (${formatUnits4(redemption.sfUnits)} j. přejde na účet success fee)`
      : formatCzk(redemption.sfFee)
    : ''
  const exit = redemption ? formatCzk(redemption.exitFee) : ''

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Potvrdit odkup {redemption ? redemption.id : ''}</DialogTitle>
      <DialogContent>
        {secondStep ? (
          <DialogContentText sx={{ color: 'error.main' }}>
            Opravdu? Tato akce je nevratná. Klientovi {redemption?.clientName} se prodá {units}{' '}
            jednotek fondu {redemption?.fundKey}, strhne se odměna {fee} a poplatek {exit} a k
            výplatě zůstane {net}. Celkový počet jednotek fondu se sníží o odkoupenou část.
          </DialogContentText>
        ) : (
          <DialogContentText>
            Zapíše se do core prodej {units} jednotek fondu {redemption?.fundKey}, srážka
            výkonnostní odměny {fee} a výstupního poplatku {exit}; k výplatě zůstane {net}. Peníze
            se odesílají až krokem „Vyplaceno".
          </DialogContentText>
        )}
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Poslat klientovi rozpis odkupu e-mailem (kopie distributorovi a na klient@apexcapital.cz)"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose} disabled={submitting}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          color={secondStep ? 'error' : 'primary'}
          onClick={handleConfirm}
          disabled={submitting}
        >
          {secondStep ? 'Ano, opravdu potvrdit' : 'Potvrdit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
